import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { SxProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import TurntableImageBox from "designSystem/components/box/TurntableImageBox";
import { black, white } from "designSystem/styles/color";
import { IconButton } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect, useRef } from "react";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import {
	useLightboxStore, ZOOM_DISABLE_ANIMATION
} from "./LightboxStore";

const LIGHT_BOX_ARROW_STYLES: SxProps = {
	backgroundColor: black[50],
	opacity: 0.5,
	position: "absolute",
	"&:hover": {
		backgroundColor: black[50],
		opacity: 0.5,
	},
	"&:active": {
		backgroundColor: black[50],
		opacity: 0.5,
	},
};

const CenteredArrowBackward = styled(IconButton)(({ theme }) => ({
	left: theme.spacing(5),
	...LIGHT_BOX_ARROW_STYLES,
}));

const CenteredArrowForward = styled(IconButton)(({ theme }) => ({
	right: theme.spacing(5),
	...LIGHT_BOX_ARROW_STYLES,
}));

const ArrowBack = styled(ArrowBackIcon)(() => ({
	color: white[50],
}));

const ArrowForward = styled(ArrowForwardIcon)(() => ({
	color: white[50],
}));

const CURSOR_STYLES = {
	grab: {
		"&:active": {
			cursor: "grabbing",
		},
	},
};

export const LBTurntableImageBox: FunctionComponent = observer(function LBTurntableImageBox() {
	const {
		currentImage,
		cursor,
		handleReactMouseScroll,
		imageContainerHeight,
		imageContainerWidth,
		imageLoaded,
		isFitPercentageScrollMode,
		isZooming,
		minWidthHeightImageContainer,
		numImages,
		onImageLoaded,
		panningOptions,
		progressiveImageData,
		derivedZoomAmountAsScale,
		setNextImage,
		setPrevImage,
	} = useLightboxStore();
	const {
		isTurntable,
		lightboxGallerySrc,
		lightboxSrc,
	} = currentImage.data;
	const turntableImageBoxRef = useRef<ReactZoomPanPinchRef>(null);

	useEffect(() => {
		turntableImageBoxRef.current?.centerView(derivedZoomAmountAsScale, ZOOM_DISABLE_ANIMATION);
	}, [imageContainerHeight, imageContainerWidth, derivedZoomAmountAsScale]);

	return (
		<>
			<TurntableImageBox
				disableTurntable={isZooming}
				hideIcon
				imageList={isTurntable ? lightboxGallerySrc : [lightboxSrc]}
				imageLoaded={imageLoaded}
				minWidthHeightImageContainer={minWidthHeightImageContainer}
				onImageLoaded={onImageLoaded}
				onMouseScroll={handleReactMouseScroll}
				panning={panningOptions}
				progressiveImageData={progressiveImageData}
				ref={turntableImageBoxRef}
				sx={{
					cursor: cursor,
					width: isFitPercentageScrollMode ? "100%" : null,
					...CURSOR_STYLES[cursor],
				}}
			/>
			{(numImages > 1) && (
				<>
					<CenteredArrowBackward onClick={setPrevImage}>
						<ArrowBack />
					</CenteredArrowBackward>
					<CenteredArrowForward onClick={setNextImage}>
						<ArrowForward />
					</CenteredArrowForward>
				</>
			)}
		</>
	);
});

export default LBTurntableImageBox;